import { HttpClient } from '@angular/common/http';
import { computed, inject, Service, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { Translation } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { APP_LANGUAGES, DEFAULT_LANGUAGE, LANGUAGE_CACHE_KEY } from '../constants/lang.constants';
import { CacheService } from './cache.service';

@Service()
export class LanguageService {
  private readonly translateService = inject(TranslateService);
  private readonly cacheService = inject(CacheService);
  private readonly httpClient = inject(HttpClient);
  private readonly primeNG = inject(PrimeNG);
  private readonly activeLanguageSignal = signal(DEFAULT_LANGUAGE);
  private readonly isRTLSignal = signal<boolean>(false);

  readonly isRTL = computed<boolean>(() => this.isRTLSignal());
  readonly activeLanguage = computed<string>(() => this.activeLanguageSignal());

  setupLanguages() {
    const savedLanguage = this.cacheService.getItem<string>(LANGUAGE_CACHE_KEY);
    const selectedLanguage = savedLanguage || DEFAULT_LANGUAGE;

    this.translateService.addLangs(APP_LANGUAGES);
    this.applyLayoutForLanguage(selectedLanguage);
    this.loadPrimeNgTranslation(selectedLanguage);
    this.translateService.use(selectedLanguage).subscribe(() => {
      this.activeLanguageSignal.set(selectedLanguage);
    });
  }

  changeLanguage(language: string) {
    this.cacheService.setItem(LANGUAGE_CACHE_KEY, language);
    this.applyLayoutForLanguage(language);
    this.loadPrimeNgTranslation(language);
    this.translateService.use(language).subscribe(() => {
      this.activeLanguageSignal.set(language);
    });
  }

  private loadPrimeNgTranslation(language: string) {
    this.httpClient
      .get<Translation>(`/i18n/primeng/${language}.json`)
      .subscribe((translation) => this.primeNG.setTranslation(translation));
  }

  private applyLayoutForLanguage(language: string) {
    this.changeDirection(language);
    this.isRTLSignal.set(this.checkRTL(language));
  }

  private checkRTL(lang: string) {
    return lang === 'ar';
  }

  private changeDirection(lang: string) {
    const dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', lang);
    document.body.style.fontFamily = lang === 'ar' ? 'Tajawal' : 'Roboto';
  }
}
